<template>
	<div class="invoice-page">
		<div class="invoice-filter-wrap" v-if="view === 'list' || view === 'reports'">
			<portal-filter-bar :label="$t('global.filter')">
				<portal-search-field
					v-if="view === 'list'"
					v-model="filter"
					:label="$t('global.filter')"
					:placeholder="$t('global.filter')"
					:clear-label="$t('global.clear')"
				></portal-search-field>
				<portal-select-field
					v-model="dateFilter"
					:label="$t('global.date')"
					:options="dateOptions"
				></portal-select-field>
			</portal-filter-bar>
		</div>

    <div class="invoice-content show-scrollbar">
      <invoice-table
        :invoices="dateFilteredInvoices"
        v-show="view === 'list'"
        ref="InvoiceTable"
        :filter="filter"
        @refresh="getInvoices"
      ></invoice-table>

      <invoice-charts
        :invoices="dateFilteredInvoices"
        :projects="projects"
        :deposit-account="depositAccount"
        v-if="view === 'reports' && $store.getters.getFeatures.summaryFinancialsEnabled"
        ref="InvoiceChart"
      ></invoice-charts>

			<payment-plan-list
				v-if="view === 'plans'"
				:plans="paymentPlans"
				:loading="paymentPlansLoading"
				:load-error="paymentPlansLoadError"
				:highlighted-plan-id="plan"
				@refresh="getPaymentPlans"
			></payment-plan-list>

      <payment-methods :ach-enabled="achEnabled" v-show="view === 'accounts'"></payment-methods>
      <subscription-list v-if="view === 'subscriptions'"></subscription-list>
    </div>
	</div>
</template>

<script>
	import InvoiceTable from '@/modules/invoices/InvoiceTable';
	import InvoiceService from '@/modules/invoices/InvoiceService';
	import InvoiceCharts from '@/modules/invoices/InvoiceCharts';
	import PaymentPlanList from '@/modules/invoices/PaymentPlanList';
	import PaymentPlanService from '@/modules/invoices/PaymentPlanService';
	import { DateTime } from 'luxon';
	import ProjectService from '@/modules/projects/ProjectService';
	import FilterHelpers from '@/utils/FilterHelpers';
	import PaymentMethods from '@/modules/invoices/PaymentMethods';
  import SubscriptionList from '@/modules/subscriptions/SubscriptionList';
  import SubscriptionService from '@/modules/subscriptions/SubscriptionService';
	import PortalFilterBar from '@/components/filters/PortalFilterBar';
	import PortalSearchField from '@/components/filters/PortalSearchField';
	import PortalSelectField from '@/components/filters/PortalSelectField';

	export default {
		name: 'InvoiceList',

		components: {
			SubscriptionList,
			InvoiceCharts,
			InvoiceTable,
			PaymentPlanList,
			PaymentMethods,
			PortalFilterBar,
			PortalSearchField,
			PortalSelectField,
		},

		props: ['v', 'plan'],

		mixins: [],

		data: function() {
			return {
				filter: null,
				dateFilter: null,
				earliest: null,
				latest: null,
				view: 'list',
				achEnabled: false,
        subscriptionsEnabled: false,
				invoices: [],
				paymentPlans: [],
				paymentPlansLoading: false,
				paymentPlansLoadError: false,
				projects: [],
				depositAccount: null,
				invoiceService: new InvoiceService(),
				paymentPlanService: new PaymentPlanService(),
				projectService: new ProjectService(),
        subscriptionService: new SubscriptionService(),

				dateOptions: FilterHelpers.getDateOptions([
					'this-month',
					'last-month',
					'past-60-days',
					'past-90-days',
					'past-6-months',
					'past-12-months',
					'this-year',
					'last-year',
				]),
			};
		},

		mounted() {
			if (this.v) {
				this.view = this.v;
			}

			this.getInvoices();
			this.getPaymentPlans();
			this.getProjects();
			this.getDeposits();
      this.checkAchEnabled();
      this.checkSubscriptionEnabled();
		},

		beforeDestroy() {},

		methods: {
      setView: function(view) {
				this.view = view;
				window.history.pushState({}, document.title, this.$router.currentRoute.path + `?v=${view}`);
			},

      checkAchEnabled: function(){
        this.invoiceService.getAchEnabled().then((res) => this.achEnabled = res.data);
      },

      checkSubscriptionEnabled: function(){
        this.subscriptionService.checkSubscriptionsEnabled().then((res) => this.subscriptionsEnabled = res.data);
      },

			getInvoices: function() {
				this.invoiceService.getInvoices().then((res) => {
					this.invoices.splice(0, this.invoices.length);
					this.invoices.push(...res.data);
					this.invoices.forEach((i) => (i.statusDetail = this.getStatus(i)));
					this.isReady = true;
				});
			},

			getPaymentPlans: function() {
				this.paymentPlansLoading = true;
				this.paymentPlansLoadError = false;
				this.paymentPlanService
					.getPaymentPlans()
					.then((res) => (this.paymentPlans = res.data || []))
					.catch(() => (this.paymentPlansLoadError = true))
					.finally(() => (this.paymentPlansLoading = false));
			},

			getDeposits: function() {
				this.invoiceService.getDepositAccount().then((res) => (this.depositAccount = res.data));
			},

			getProjects: function() {
				this.projectService.getProjects().then((res) => {
					this.projects.splice(0, this.projects.length);
					this.projects.push(...res.data);
				});
			},

			getStatus(invoice) {
				let background, color, label;
				let payable = true;

				switch (invoice.status) {
					case 'REFUNDED': {
						background = '--v-blue_5-base';
						color = '--v-primary-base';
						label = this.$t('invoices.status.refunded');
						payable = false;
						break;
					}

					case 'PAID': {
						background = '--v-success_10-base';
						color = '--v-success-base';
						label = this.$t('invoices.status.paid');
						payable = false;
						break;
					}

					case 'PARTIAL':
					case 'SENT': {
						if (DateTime.fromISO(invoice.dateDue) < DateTime.now()) {
							background = '--v-alert_10-base';
							color = '--v-alert-base';
							label = this.$t('invoices.status.late');
						} else if (invoice.status === 'PARTIAL') {
							background = '--v-warning_10-base';
							color = '--v-warning-base';
							label = this.$t('invoices.status.partially-paid');
						} else {
							background = '--v-blue_5-base';
							color = '--v-gray_80-base';
							label = this.$t('invoices.status.pending');
						}

						break;
					}

					default: {
						background = '--v-gray_20-base';
						color = '--v-gray_80-base';
						label = invoice.status.charAt(0).toUpperCase() + invoice.status.toLowerCase().slice(1);
						payable = false;
						break;
					}
				}

				return {
					background,
					color,
					label,
					payable,
				};
			},
		},

		watch: {
			dateFilter: function(val) {
				if (val) {
					let helper = FilterHelpers.getEarliestAndLatest(val, false);
					this.earliest = helper.earliest;
					this.latest = helper.latest;
				} else {
					this.earliest = null;
					this.latest = null;
				}
			},

      v: function(val){
        this.setView(val);
      }
		},

		computed: {
			pages: function() {
				let result = [
					{ label: this.$t('invoices.list.heading'), value: 'list' },
					{ label: this.$t('payment-plans.heading'), value: 'plans' },
					{ label: this.$t('invoices.reports.heading'), value: 'reports' }
				];

        if(this.achEnabled){
          result.push({ label: this.$t('invoices.ach-account.heading'), value: 'accounts' })
        }

        if(this.subscriptionsEnabled){
          result.push({ label: this.$t('subscriptions.heading'), value: 'subscriptions' })
        }

        return result;
			},

			dateFilteredInvoices: function() {
				if (this.earliest && this.latest) {
					return this.invoices.filter((i) => {
						let dateSent = DateTime.fromISO(i.dateSent);
						if (dateSent >= this.earliest && dateSent <= this.latest) {
							return true;
						} else {
							return false;
						}
					});
				} else {
					return this.invoices;
				}
			},
		},
	};
</script>

<style scoped lang="scss">
	.invoice-page {
		min-height: 100%;
		display: flex;
		flex-direction: column;
	}

	.invoice-filter-wrap {
		position: sticky;
		z-index: 3;
		top: 0;
		padding: 16px 16px 0;
		background: var(--portal-canvas);
	}

	.invoice-content {
		min-height: 0;
		flex: 1;
	}

	@media (min-width: 600px) {
		.invoice-filter-wrap ::v-deep .portal-search-field {
			min-width: 220px;
			flex-basis: 240px;
		}

		.invoice-filter-wrap ::v-deep .portal-select-field {
			min-width: 164px;
			flex-basis: 196px;
		}
	}

	@media (max-width: 599px) {
		.invoice-filter-wrap {
			padding: 12px 12px 0;
		}
	}
</style>
